import React, {
    forwardRef,
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Loader, Button } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { FiltersProvider } from 'components/Filters/Context';

import {
    getDashboard,
    createWidget,
    updateWidget,
    deleteMultipleWidgets,
} from 'lib/services/forceBI';
import { publish } from 'lib/EventBuser';
import { REFRESH_FORCE_BI_VISUALS } from 'lib/events';

import { downloadMultipleSnapshotsAsZip } from './utils/downloadSnapshots';
import { successToast, errorToast } from 'utils/toast';
import { isEqual } from 'utils/objects';
import { downloadMultipleTablesAsZip } from './utils/downloadTables';

import Header from './components/Header';
import GridContainer from './components/GridContainer';
import Crud from './components/Crud';
import Empty from './components/Empty';

import { BASE_LAYOUT_SPECS, FORCE_BI_SUBMODULE } from './constants';

import useForceBIFilters from './hooks/useForceBIFilters';
import useForceBISnapshots from './hooks/useForceBISnapshots';
import useForceBIUtils from './hooks/useForceBIUtils';

import styles from './styles';

const useStyles = createUseStyles(styles, 'ForceBI');

const ForceBI = memo(
    forwardRef(
        (
            {
                entity, // only for tracking
                id,
                title,
                googleMapsApiKey,
                filtersComponent,
                filtersAddButtonSchema,
                defaultCrudFilters,
                defaultDashboardFilters,
                customDashboardFilters,
                canUseEmail,
                showEmailEditor,
                canEdit: isEditable,
                standardFieldsSchemaMap,
                extraFieldsSchemaMap,
                rangeFields,
                crossFilterIdSeparator,
                formatDate,
                getEntitiesManager,
                constants,
                hashHistory,
                ensureRoute,
                forceBIId,
            },
            ref,
        ) => {
            // --- Context & hooks
            const classes = useStyles();
            const { t, logEvent, entities } = useWeb5Context();
            const forceBIUtils = useForceBIUtils({
                standardFieldsSchemaMap,
                extraFieldsSchemaMap,
                rangeFields,
                crossFilterIdSeparator,
                formatDate,
                getEntitiesManager,
            });

            // --- State
            const [isLoading, setIsLoading] = useState(true);
            const [widgets, setWidgets] = useState([]);
            const [isCrudOpen, setIsCrudOpen] = useState(false);
            const [crudData, setCrudData] = useState(null);
            const [selected, setSelected] = useState([]);
            const [isError, setIsError] = useState(false);
            const isFirstRender = useRef(true);
            const gridUtilsRef = useRef(null);
            const debounceTimeoutRef = useRef(null);
            const [areFiltersSaved, setAreFiltersSaved] = useState(true);
            const [areFiltersChanged, setAreFiltersChanged] = useState(false);

            // --- Derived state (cache, permissions, filter/snapshot hooks)
            // If has Custom Dashboard Filters means that this Dashboard is being consumed from an EntityDetail
            // The user won't be able to make any CRUD actions
            const hasCustomDashboardFilters = useMemo(
                () =>
                    (customDashboardFilters && Object.keys(customDashboardFilters).length > 0) ||
                    false,
                [customDashboardFilters],
            );

            const defaultCache = useMemo(() => {
                return {
                    visibleFilters: [...(defaultDashboardFilters?.visibleFilters || [])],
                };
            }, [defaultDashboardFilters]);

            const afterOnChange = useCallback(
                ({ filters, crossFilters, operators, crossOperators }) => {
                    if (debounceTimeoutRef.current) {
                        clearTimeout(debounceTimeoutRef.current);
                    }

                    logEvent({
                        event: entities.ANALYTICS_REPORT.trueName,
                        functionality: 'filter',
                        submodule: FORCE_BI_SUBMODULE,
                    });

                    // When changing dashboard we need to resetLoaded visuals
                    const { resetWidgets } = gridUtilsRef.current;
                    debounceTimeoutRef.current = setTimeout(() => {
                        setAreFiltersSaved(false);
                        setAreFiltersChanged(true);
                        resetWidgets({ filters, crossFilters, operators, crossOperators });
                    }, 300);
                },
                [logEvent, entities],
            );

            const afterOnChangeCache = useCallback(({ visibleFilters, oldVisibleFilters }) => {
                const hasVisibleFiltersChanged = !isEqual(visibleFilters, oldVisibleFilters);
                if (hasVisibleFiltersChanged) {
                    setAreFiltersSaved(false);
                    setAreFiltersChanged(true);
                }
            }, []);

            const forceBIFiltersProps = useForceBIFilters({
                defaultCache,
                isDashboard: true,
                afterOnChange,
                afterOnChangeCache,
                constants,
            });

            const dashboardFilters = useMemo(() => {
                return {
                    filters: forceBIFiltersProps.filters,
                    crossFilters: forceBIFiltersProps.crossFilters,
                    operators: forceBIFiltersProps.operators,
                    crossOperators: forceBIFiltersProps.crossOperators,
                };
            }, [
                forceBIFiltersProps.filters,
                forceBIFiltersProps.crossFilters,
                forceBIFiltersProps.operators,
                forceBIFiltersProps.crossOperators,
            ]);

            const initWidget = useCallback(
                ({ id, data, onSuccess, ...dashboardFilters }) => {
                    publish(REFRESH_FORCE_BI_VISUALS, {
                        id,
                        data,
                        onSuccess,
                        // customDashboardFilters are for filters that do not exist as a filter in the schema, but backend accepts it
                        // one case would be seeing the dashboard from company or opportunity detail and filter by the current company or opportunity
                        customDashboardFilters: customDashboardFilters || {},
                        ...(!dashboardFilters || {}),
                    });
                },
                [customDashboardFilters],
            );

            const permissions = useMemo(() => {
                const canMutate = !hasCustomDashboardFilters && isEditable;
                const canInteract = canMutate && selected.length === 0;
                return {
                    canCreate: canMutate,
                    canEdit: canMutate,
                    canDelete: canMutate,
                    canShare: canMutate,
                    canSaveFilters: canMutate,
                    canDrag: canInteract,
                    canResize: canInteract,
                };
            }, [hasCustomDashboardFilters, isEditable, selected.length]);

            const {
                getSnapshot,
                deleteSnapshot,
                getSingleSnapshot,
                getBulkSnapshots,
                beforeDownloadImages,
                updateErrorWidgets,
                snapshotsRef,
                errorWidgetsRef,
            } = useForceBISnapshots({ selected, widgets, gridUtilsRef });

            // --- Handlers: download, widget processing & dashboard init
            const onDownloadImages = useCallback(() => {
                return beforeDownloadImages().then(() => {
                    const snapshotsToDownload = getBulkSnapshots(snapshotsRef.current);

                    if (!Object.keys(snapshotsToDownload).length) {
                        errorToast({ t, text: t('label_selected_widgets_empty_no_download') });
                        Promise.resolve();
                    }

                    downloadMultipleSnapshotsAsZip({
                        snapshots: snapshotsToDownload,
                        withTitle: true,
                        t,
                        dashboardName: title,
                    }).then(() => {
                        logEvent({
                            event: entities.ANALYTICS_REPORT.trueName,
                            functionality: 'bulkAction',
                            submodule: FORCE_BI_SUBMODULE,
                        });
                        successToast({ t, text: t('label_images_downloaded') });
                    });
                }, [getBulkSnapshots, beforeDownloadImages, title, t, logEvent, entities]);
            }, [
                beforeDownloadImages,
                getBulkSnapshots,
                snapshotsRef,
                title,
                logEvent,
                entities,
                t,
            ]);

            const onDownloadTables = useCallback(() => {
                const widgetsToDownload = widgets.reduce((arr, current) => {
                    const id = parseInt(current.id, 10);
                    //Prevent to add empty Excels
                    if (selected.includes(id) && !errorWidgetsRef.current.includes(id))
                        arr.push(current);
                    return arr;
                }, []);

                return downloadMultipleTablesAsZip({
                    widgets: widgetsToDownload,
                    dashboardFilters,
                    forceBIUtils,
                    t,
                    dashboardName: title,
                }).then(() => {
                    logEvent({
                        event: entities.ANALYTICS_REPORT.trueName,
                        functionality: 'bulkAction',
                        submodule: FORCE_BI_SUBMODULE,
                    });
                });
            }, [
                selected,
                widgets,
                errorWidgetsRef,
                dashboardFilters,
                forceBIUtils,
                title,
                t,
                logEvent,
                entities,
            ]);

            const processWidget = useCallback(
                (data) => {
                    const { translateFiltersFromBackend } = forceBIUtils;

                    const { filters, crossFilters, operators, crossOperators } =
                        translateFiltersFromBackend({
                            filters: data.filters,
                            operators: data.operators,
                        });

                    const newWidget = {
                        title: data.title,
                        id: data.id.toString(),
                        dashboardId: id,
                        ...data,
                        filters,
                        crossFilters,
                        operators,
                        crossOperators,
                    };

                    return newWidget;
                },
                [id, forceBIUtils],
            );

            const mapData = useCallback(
                (data) => (data?.length ? data.map((item) => processWidget(item)) : []),
                [processWidget],
            );

            const initDashboardFilters = useCallback(
                ({ filters = {}, operators = {}, visibleFilters = [] }) => {
                    const { init } = forceBIFiltersProps;
                    const { processFiltersToInitDashboard } = forceBIUtils;

                    const initProps = processFiltersToInitDashboard({ filters, operators });

                    init({
                        filters: initProps.filters,
                        crossFilters: initProps.crossFilters,
                        operators: initProps.operators,
                        crossOperators: initProps.crossOperators,
                        visibleFilters: visibleFilters,
                        quickFilters: visibleFilters,
                    });
                },
                [forceBIFiltersProps, forceBIUtils],
            );

            const initDashboard = useCallback(() => {
                getDashboard(id)
                    .then((response) => {
                        const { filters, operators, visibleFilters } = response.data;
                        let initObj = {};

                        if (!hasCustomDashboardFilters) {
                            initObj = {
                                filters: filters || {},
                                operators: operators || {},
                                visibleFilters: visibleFilters || [],
                            };
                        }

                        initDashboardFilters(initObj);

                        setIsLoading(false);

                        const widgets = mapData(response.data.widgets);

                        setWidgets(widgets);
                    })
                    .catch((error) => {
                        setIsLoading(false);
                        setIsError(true);
                    });
            }, [id, mapData, initDashboardFilters, hasCustomDashboardFilters]);

            const refreshDashboard = useCallback(() => {
                setIsLoading(true);
                setIsError(false);
                initDashboard();
            }, [initDashboard]);

            // --- Effects
            useImperativeHandle(ref, () => ({ refreshDashboard }), [refreshDashboard]);
            useEffect(() => {
                if (isFirstRender?.current) {
                    isFirstRender.current = false;
                    initDashboard();

                    if (!entity) return;

                    let event = entities.ANALYTICS_REPORT.trueName;
                    const { ANALYTICS_REPORT, DASHBOARD } = entities;

                    if (entity.entity === ANALYTICS_REPORT.entity) {
                        event = entities.ANALYTICS_REPORT.trueName;
                    } else if (entity.entity === DASHBOARD.entity) {
                        event = entities.DASHBOARD.trueName;
                    }

                    // If there is no event, means that is being consumed from a company or opportunity detail
                    if (event) {
                        logEvent({
                            event,
                            functionality: 'detailView',
                            submodule: FORCE_BI_SUBMODULE,
                        });
                    }
                }
            }, [initDashboard, refreshDashboard, entities, entity, logEvent]);

            // --- Handlers: selection, CRUD, grid
            const onSelect = useCallback(
                (id) => {
                    if (!id) return;
                    let newSelected = [...selected];
                    if (newSelected.includes(id)) {
                        newSelected = newSelected.filter((current) => current !== id);
                    } else {
                        newSelected.push(id);
                    }

                    setSelected(newSelected);
                },
                [selected],
            );

            const onSelectAll = useCallback(() => {
                if (selected.length) {
                    setSelected([]);
                    return;
                }
                const all = widgets.map((current) => current.id);
                setSelected(all);
            }, [widgets, selected]);

            const onClearSelected = useCallback(
                (ids) => {
                    if (ids?.length) {
                        const newSelected = selected.filter((current) => !ids.includes(current));
                        setSelected(newSelected);
                    } else setSelected([]);
                },
                [selected],
            );

            const onOpenCreate = useCallback(() => {
                setIsCrudOpen(true);
            }, []);

            const onCloseCrud = useCallback(() => {
                setIsCrudOpen(false);
            }, []);

            const onAfterCloseCrud = useCallback(() => {
                if (crudData) setCrudData(null);
            }, [crudData]);

            const onEdit = useCallback(
                (newSelected) => {
                    setIsCrudOpen(true);
                    const { processDataToInitCrud } = forceBIUtils;
                    const newCrudData = processDataToInitCrud(newSelected);
                    setCrudData(newCrudData);
                },
                [forceBIUtils],
            );

            const onChangeCrud = useCallback((data) => {
                setCrudData(data);
            }, []);

            const afterDeleteWidget = useCallback(
                (ids) => {
                    const newWidgets = widgets.filter((current) => !ids.includes(current.id));
                    setWidgets(newWidgets);

                    const isDeleteMultiple = ids.length > 1;

                    if (isDeleteMultiple) {
                        selected.forEach((current) => deleteSnapshot(current));
                        onClearSelected();
                        return;
                    } else {
                        deleteSnapshot(ids[0]);
                        onClearSelected(ids);
                    }
                    successToast({ t, text: t('label_success_delete') });
                },
                [widgets, onClearSelected, selected, deleteSnapshot, t],
            );

            const onDeleteMultiple = useCallback(() => {
                return new Promise((resolve, reject) => {
                    deleteMultipleWidgets(selected)
                        .then(() => {
                            afterDeleteWidget(selected);
                            logEvent({
                                event: entities.ANALYTICS_REPORT.trueName,
                                functionality: 'bulkAction',
                                submodule: FORCE_BI_SUBMODULE,
                            });
                            resolve();
                        })
                        .catch(() => {
                            errorToast({ t, text: t('label_error_delete') });
                            reject();
                        });
                });
            }, [t, selected, afterDeleteWidget, logEvent, entities]);

            const onGridInit = useCallback((utils) => {
                gridUtilsRef.current = utils;
            }, []);

            const calculateNewWidgetPosition = useCallback(() => {
                let newItem;

                if (!widgets.length) {
                    newItem = { ...BASE_LAYOUT_SPECS, x: 0, y: 0 };
                } else {
                    const { findEmptySpot, getLastRow } = gridUtilsRef.current;
                    const { w, h } = BASE_LAYOUT_SPECS;
                    const emptySpot = findEmptySpot({ w, h });
                    if (emptySpot) {
                        newItem = { ...BASE_LAYOUT_SPECS, ...emptySpot };
                    } else {
                        const lastRow = getLastRow();
                        newItem = { ...BASE_LAYOUT_SPECS, x: 0, y: lastRow + 1 };
                    }
                }

                return newItem;
            }, [widgets]);

            const onChangeGridWidgets = useCallback(
                ({ modifiedWidgets, widgets }) => {
                    setWidgets(widgets);
                    modifiedWidgets.map((current) => {
                        const { processDataToSendFromCrud } = forceBIUtils;

                        let finalData = processDataToSendFromCrud({
                            data: current,
                        });

                        return updateWidget(current.id, finalData);
                    });
                },
                [forceBIUtils],
            );

            const onConfirmCrud = useCallback(
                ({ data }) => {
                    return new Promise((resolve, reject) => {
                        if (data?.id) {
                            updateWidget(data.id, data)
                                .then((response) => {
                                    if (response.data) {
                                        const newWidget = processWidget(response.data);
                                        const newWidgets = widgets.map((current) => {
                                            if (
                                                parseInt(current.id, 10) ===
                                                parseInt(newWidget.id, 10)
                                            ) {
                                                return newWidget;
                                            }
                                            return current;
                                        });

                                        setWidgets(newWidgets);

                                        initWidget({
                                            id: data.id,
                                            data: newWidget,
                                            ...dashboardFilters,
                                        });
                                    }
                                    setCrudData(null);
                                    successToast({ t, text: t('label_success_save') });
                                    onCloseCrud();
                                    resolve();
                                })
                                .catch(() => {
                                    errorToast({ t, text: t('label_error_save') });
                                    reject();
                                });
                        } else {
                            const newPosition = calculateNewWidgetPosition();
                            const newData = {
                                dashboardId: id,
                                ...data,
                                position: newPosition,
                            };

                            createWidget(newData)
                                .then((response) => {
                                    if (response.data) {
                                        const newWidget = processWidget(response.data);
                                        const newWidgets = [...widgets];
                                        newWidgets.push(newWidget);
                                        setWidgets(newWidgets);
                                    }
                                    successToast({ t, text: t('label_success_save') });
                                    setCrudData(null);
                                    onCloseCrud();
                                    logEvent({
                                        event: entities.ANALYTICS_REPORT.trueName,
                                        functionality: 'createWidget',
                                        submodule: FORCE_BI_SUBMODULE,
                                    });
                                    resolve();
                                })
                                .catch(() => {
                                    errorToast({ t, text: t('label_error_save') });
                                    reject();
                                });
                        }
                    });
                },
                [
                    calculateNewWidgetPosition,
                    id,
                    onCloseCrud,
                    t,
                    widgets,
                    processWidget,
                    initWidget,
                    dashboardFilters,
                    logEvent,
                    entities,
                ],
            );

            const afterDuplicateWidget = useCallback(
                (data) => {
                    if (data) {
                        const newWidget = processWidget(data);
                        const newWidgets = [...widgets];
                        newWidgets.push(newWidget);

                        setWidgets(newWidgets);
                    }
                },
                [processWidget, widgets],
            );

            // --- Derived (for render)
            const isEmpty = !widgets?.length;

            const onOpenShare = useCallback(
                (url) => {
                    showEmailEditor({
                        content: `${t('label_forcebi_dashboard_share')} <a href=${url}>${url}</a>`,
                        signatureAtEnd: true,
                    });
                    logEvent({
                        event: entities.ANALYTICS_REPORT.trueName,
                        functionality: 'shareToEmail',
                        submodule: FORCE_BI_SUBMODULE,
                    });
                },
                [showEmailEditor, t, logEvent, entities],
            );

            const onOpenBulkShare = useCallback(() => {
                return beforeDownloadImages().then(() => {
                    let defaults = {};
                    const snapshotsToDownload = getBulkSnapshots(snapshotsRef.current);
                    if (Object.keys(snapshotsToDownload).length) {
                        let attachments = Object.entries(snapshotsToDownload).reduce(
                            (arr, [id, data]) => {
                                arr.push({ data: data.image, name: data.title });
                                return arr;
                            },
                            [],
                        );
                        defaults.attachments = attachments;
                    }
                    logEvent({
                        event: entities.ANALYTICS_REPORT.trueName,
                        functionality: 'bulkAction',
                        submodule: FORCE_BI_SUBMODULE,
                    });
                    showEmailEditor(defaults);
                });
            }, [
                showEmailEditor,
                getBulkSnapshots,
                beforeDownloadImages,
                snapshotsRef,
                logEvent,
                entities,
            ]);

            const hasWidgets = Boolean(widgets?.length);

            // --- Memoized props (for child components)
            const headerProps = useMemo(
                () => ({
                    dashboardId: id,
                    entity: forceBIUtils.entity,
                    filtersComponent,
                    filtersAddButtonSchema,
                    onOpenCreate,
                    onSelectAll,
                    widgets,
                    selected,
                    onDeleteMultiple,
                    onClearSelected,
                    onOpenShare,
                    onOpenBulkShare,
                    onDownloadImages,
                    onDownloadTables,
                    canUseEmail,
                    canShare: permissions.canShare,
                    canCreate: permissions.canCreate,
                    canDelete: permissions.canDelete,
                    canSaveFilters: permissions.canSaveFilters,
                    forceBIFiltersProps,
                    forceBIUtils,
                    hasWidgets,
                    areFiltersSaved,
                    setAreFiltersSaved,
                    areFiltersChanged,
                    isLoading,
                    customDashboardFilters,
                    hashHistory,
                    ensureRoute,
                    forceBIId,
                }),
                [
                    id,
                    onOpenCreate,
                    forceBIUtils,
                    filtersComponent,
                    filtersAddButtonSchema,
                    onSelectAll,
                    widgets,
                    selected,
                    onDeleteMultiple,
                    onClearSelected,
                    onOpenShare,
                    onOpenBulkShare,
                    onDownloadImages,
                    onDownloadTables,
                    canUseEmail,
                    permissions.canShare,
                    permissions.canCreate,
                    permissions.canDelete,
                    permissions.canSaveFilters,
                    forceBIFiltersProps,
                    hasWidgets,
                    areFiltersSaved,
                    setAreFiltersSaved,
                    areFiltersChanged,
                    isLoading,
                    customDashboardFilters,
                    hashHistory,
                    ensureRoute,
                    forceBIId,
                ],
            );

            const gridContainerProps = useMemo(
                () => ({
                    widgets,
                    onEdit,
                    onSelect,
                    selected,
                    googleMapsApiKey,
                    getSnapshot,
                    getSingleSnapshot,
                    canUseEmail,
                    showEmailEditor,
                    dashboardFilters,
                    forceBIUtils,
                    onChangeGridWidgets,
                    initGrid: onGridInit,
                    initWidget,
                    afterDeleteWidget,
                    afterDuplicateWidget,
                    calculateNewWidgetPosition,
                    canDrag: permissions.canDrag,
                    canResize: permissions.canResize,
                    canCreate: permissions.canCreate,
                    canEdit: permissions.canEdit,
                    canDelete: permissions.canDelete,
                    canShare: permissions.canShare,
                    customDashboardFilters,
                    updateErrorWidgets,
                }),
                [
                    widgets,
                    onEdit,
                    onSelect,
                    selected,
                    googleMapsApiKey,
                    getSnapshot,
                    getSingleSnapshot,
                    canUseEmail,
                    showEmailEditor,
                    dashboardFilters,
                    forceBIUtils,
                    onChangeGridWidgets,
                    onGridInit,
                    initWidget,
                    afterDeleteWidget,
                    afterDuplicateWidget,
                    calculateNewWidgetPosition,
                    permissions.canDrag,
                    permissions.canResize,
                    permissions.canCreate,
                    permissions.canEdit,
                    permissions.canDelete,
                    permissions.canShare,
                    customDashboardFilters,
                    updateErrorWidgets,
                ],
            );

            const crudProps = useMemo(
                () => ({
                    isOpen: isCrudOpen,
                    data: crudData,
                    onClose: onCloseCrud,
                    onAfterClose: onAfterCloseCrud,
                    onConfirm: onConfirmCrud,
                    onChange: onChangeCrud,
                    googleMapsApiKey,
                    filtersComponent,
                    filtersAddButtonSchema,
                    defaultFilters: defaultCrudFilters,
                    forceBIUtils,
                    constants,
                    crossFilterIdSeparator,
                }),
                [
                    isCrudOpen,
                    crudData,
                    onCloseCrud,
                    onAfterCloseCrud,
                    onConfirmCrud,
                    onChangeCrud,
                    googleMapsApiKey,
                    filtersComponent,
                    filtersAddButtonSchema,
                    defaultCrudFilters,
                    forceBIUtils,
                    constants,
                    crossFilterIdSeparator,
                ],
            );

            // --- Render
            return (
                <FiltersProvider>
                    <div className={classes.root}>
                        <Header {...headerProps} />
                        <div className={classes.content}>
                            {isLoading && (
                                <div className={classes.loader}>
                                    <Loader size="big" />
                                </div>
                            )}
                            {!isLoading && isEmpty && permissions.canCreate && (
                                <Empty type="empty">
                                    <Button className={classes.emptyButton} onClick={onOpenCreate}>
                                        {t('label_forcebi_crud_title_placeholder')}
                                    </Button>
                                </Empty>
                            )}
                            {!isLoading && isEmpty && !permissions.canCreate && (
                                <Empty type="emptyNoPermission" />
                            )}
                            {!isLoading && isError && (
                                <Empty type="error">
                                    <Button
                                        className={classes.errorButton}
                                        onClick={refreshDashboard}
                                    >
                                        {t('action_refresh')}
                                    </Button>
                                </Empty>
                            )}
                            {!isLoading && !isEmpty && !isError && (
                                <GridContainer {...gridContainerProps} />
                            )}
                            <Crud {...crudProps} />
                        </div>
                    </div>
                </FiltersProvider>
            );
        },
    ),
);

ForceBI.displayName = 'ForceBI';

ForceBI.propTypes = {
    entity: PropTypes.object,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string,
    googleMapsApiKey: PropTypes.string,
    filtersComponent: PropTypes.elementType,
    filtersAddButtonSchema: PropTypes.object,
    defaultCrudFilters: PropTypes.object,
    defaultDashboardFilters: PropTypes.object,
    customDashboardFilters: PropTypes.object,
    canUseEmail: PropTypes.bool,
    showEmailEditor: PropTypes.func,
    canEdit: PropTypes.bool,
    standardFieldsSchemaMap: PropTypes.object,
    extraFieldsSchemaMap: PropTypes.object,
    rangeFields: PropTypes.array,
    crossFilterIdSeparator: PropTypes.string,
    formatDate: PropTypes.func,
    getEntitiesManager: PropTypes.func,
    constants: PropTypes.object,
    hashHistory: PropTypes.object,
    ensureRoute: PropTypes.func,
    forceBIId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ForceBI;
